import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, X } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useLyraContext } from '@/context/LyraContext'

export const lyraUsedKey = (userId: string) => `translyx_lyra_used_${userId}`
export const LYRA_USED_EVENT = 'cat:lyra-used'
const dismissedKey = (userId: string) => `translyx_onboarding_dismissed_${userId}`
const startedKey = (userId: string) => `translyx_onboarding_started_${userId}`

function readFlag(key: string): boolean {
  try { return localStorage.getItem(key) === '1' } catch { return false }
}
function writeFlag(key: string) {
  try { localStorage.setItem(key, '1') } catch { /* ignore */ }
}

// Brings the card back (used by the Help & Guide page).
export function showGettingStarted(userId: string) {
  try {
    localStorage.removeItem(dismissedKey(userId))
    localStorage.setItem(startedKey(userId), '1')
  } catch { /* ignore */ }
}

// The dashboard heading shows a "Resume setup" pill while the card is hidden.
// The card owns the state and broadcasts it; the pill and card talk by event.
const STATE_EVENT = 'cat:onboarding-state'
const RESTORE_EVENT = 'cat:onboarding-restore'
interface OnboardingState { resumable: boolean; doneCount: number; total: number }
let lastState: OnboardingState = { resumable: false, doneCount: 0, total: 4 }

export function ResumeSetupPill() {
  const [state, setState] = useState<OnboardingState>(lastState)
  useEffect(() => {
    const on = (e: Event) => setState((e as CustomEvent<OnboardingState>).detail)
    window.addEventListener(STATE_EVENT, on)
    setState(lastState)
    return () => window.removeEventListener(STATE_EVENT, on)
  }, [])
  if (!state.resumable) return null
  return (
    <button type="button" className="gs-resume" onClick={() => window.dispatchEvent(new Event(RESTORE_EVENT))}>
      Resume setup &middot; {state.doneCount} of {state.total} done
    </button>
  )
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

const EXIT_MS = 380
const CELEBRATE_MS = 4000
const TOAST_MS = 6000

interface GettingStartedCardProps {
  userId: string
  firstName: string
  hasProjects: boolean
  hasConfirmedSegment: boolean
}

export default function GettingStartedCard({ userId, firstName, hasProjects, hasConfirmedSegment }: GettingStartedCardProps) {
  const { setLyraOpen } = useLyraContext()
  const [dismissed, setDismissed] = useState(() => readFlag(dismissedKey(userId)))
  const [lyraUsed, setLyraUsed] = useState(() => readFlag(lyraUsedKey(userId)))
  const [glossaryCount, setGlossaryCount] = useState<number | null>(null)
  const [exiting, setExiting] = useState(false)
  const [gone, setGone] = useState(() => readFlag(dismissedKey(userId)))
  const [toast, setToast] = useState(false)
  const [celebrating, setCelebrating] = useState(false)
  const [popped, setPopped] = useState<Record<number, boolean>>({})
  const baseline = useRef<boolean[] | null>(null)
  const timers = useRef<number[]>([])

  // The card is for people starting out. Once it has been shown to someone with
  // no projects it stays until they finish or dismiss it; people who already
  // have projects when this ships never see it.
  const started = readFlag(startedKey(userId))
  const eligible = !hasProjects || started

  useEffect(() => {
    if (!hasProjects) writeFlag(startedKey(userId))
  }, [hasProjects, userId])

  useEffect(() => {
    const onLyra = () => setLyraUsed(true)
    window.addEventListener(LYRA_USED_EVENT, onLyra)
    return () => window.removeEventListener(LYRA_USED_EVENT, onLyra)
  }, [])

  useEffect(() => () => { timers.current.forEach(t => window.clearTimeout(t)) }, [])

  const restore = () => {
    timers.current.forEach(t => window.clearTimeout(t))
    timers.current = []
    try { localStorage.removeItem(dismissedKey(userId)) } catch { /* ignore */ }
    setDismissed(false)
    setExiting(false)
    setGone(false)
    setToast(false)
  }
  const restoreRef = useRef(restore)
  restoreRef.current = restore
  useEffect(() => {
    const on = () => restoreRef.current()
    window.addEventListener(RESTORE_EVENT, on)
    return () => window.removeEventListener(RESTORE_EVENT, on)
  }, [])

  useEffect(() => {
    if (!eligible) return
    let cancelled = false
    ;(async () => {
      const { count, error } = await supabase
        .from('terminology')
        .select('id', { count: 'exact', head: true })
        .eq('created_by', userId)
      if (!cancelled) setGlossaryCount(error ? 0 : count ?? 0)
    })()
    return () => { cancelled = true }
  }, [eligible, userId])

  const steps = [
    {
      title: 'Create your first project',
      hint: 'Upload a DOCX, PDF, XLIFF or TXT file.',
      done: hasProjects,
      to: '/projects/new',
      cta: 'Create project',
    },
    {
      title: 'Translate a segment with machine translation',
      hint: 'Open a project, pick a segment and confirm the translation.',
      done: hasConfirmedSegment,
      to: hasProjects ? '/projects' : '/projects/new',
      cta: hasProjects ? 'Open projects' : 'Create project',
    },
    {
      title: 'Add a term to your glossary',
      hint: 'Keep key terminology consistent across projects.',
      done: (glossaryCount ?? 0) > 0,
      to: '/glossary',
      cta: 'Open glossary',
    },
    {
      title: 'Ask Lyra for help with a tricky segment',
      hint: 'Lyra, our AI Assistant, sits in the sidebar.',
      done: lyraUsed,
      onClick: () => setLyraOpen(true),
      cta: 'Ask Lyra',
    },
  ]
  const doneFlags = steps.map(s => s.done)
  const doneCount = doneFlags.filter(Boolean).length
  const allDone = doneCount === steps.length
  const ready = glossaryCount !== null

  const exit = (afterMs = 0) => {
    const run = () => {
      setExiting(true)
      timers.current.push(window.setTimeout(() => setGone(true), prefersReducedMotion() ? 0 : EXIT_MS))
    }
    if (afterMs > 0) timers.current.push(window.setTimeout(run, afterMs))
    else run()
  }

  // Compare against what was already done when the card first appeared, so only
  // steps completed while it is on screen pop, and only that run can celebrate.
  useEffect(() => {
    if (!ready) return
    if (baseline.current === null) {
      baseline.current = doneFlags
      return
    }
    const newly: Record<number, boolean> = {}
    doneFlags.forEach((d, i) => { if (d && !baseline.current![i]) newly[i] = true })
    if (Object.keys(newly).length) setPopped(p => ({ ...p, ...newly }))
    if (allDone && !celebrating && baseline.current.some(d => !d)) {
      setCelebrating(true)
      exit(CELEBRATE_MS)
    }
  }, [ready, doneFlags.join(',')])

  // Tell the dashboard whether to offer "Resume setup".
  const resumable = eligible && ready && dismissed && !allDone
  useEffect(() => {
    lastState = { resumable, doneCount, total: steps.length }
    window.dispatchEvent(new CustomEvent(STATE_EVENT, { detail: lastState }))
  }, [resumable, doneCount, steps.length])
  useEffect(() => () => {
    lastState = { resumable: false, doneCount: 0, total: 4 }
    window.dispatchEvent(new CustomEvent(STATE_EVENT, { detail: lastState }))
  }, [])

  // Hide the dismissal toast after a few seconds.
  useEffect(() => {
    if (!toast) return
    const t = window.setTimeout(() => setToast(false), TOAST_MS)
    return () => window.clearTimeout(t)
  }, [toast])

  const dismiss = () => {
    writeFlag(dismissedKey(userId))
    setDismissed(true)
    setToast(true)
    exit()
  }

  const toastEl = toast ? (
    <div className="gs-toast" role="status">
      <span>Getting started is hidden. Bring it back any time with Resume setup on your dashboard, or from Help &amp; Guide.</span>
      <button type="button" className="gs-toast-undo" onClick={restore}>Undo</button>
    </div>
  ) : null

  const hideCard =
    !eligible || gone || !ready ||
    // Everything was already done when the page loaded: nothing to show.
    (allDone && !celebrating && (baseline.current === null || baseline.current.every(Boolean)))
  if (hideCard) return toastEl

  const pct = Math.round((doneCount / steps.length) * 100)

  return (
    <>
    <div className={'gs-collapse' + (exiting ? ' gs-exiting' : '')}>
      <div className="gs-wrap">
        <section className={'gs-card' + (celebrating ? ' gs-glow' : '')} aria-label="Getting started">
          <span className="gs-orb gs-orb-gold" aria-hidden="true" />
          <span className="gs-orb gs-orb-white" aria-hidden="true" />
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="gs-title">Welcome to TranslyxCAT, {firstName}</h2>
              <p className="gs-sub">{celebrating ? "You're all set." : 'Four quick steps to get the most from it.'}</p>
            </div>
            <button type="button" onClick={dismiss} className="gs-x" aria-label="Dismiss getting started">
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <div className="gs-progress-row">
            <div className="gs-track" role="progressbar" aria-valuemin={0} aria-valuemax={steps.length} aria-valuenow={doneCount} aria-label="Getting started progress">
              <div className="gs-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="gs-count" aria-live="polite">{doneCount} of {steps.length} done</span>
          </div>

          <ol className="gs-steps">
            {steps.map((step, i) => (
              <li key={step.title} className={'gs-step' + (step.done ? ' gs-step-done' : '')} style={{ animationDelay: `${120 + i * 70}ms` }}>
                <span className={'gs-badge' + (step.done ? ' gs-badge-done' : '') + (popped[i] ? ' gs-pop' : '')}>
                  {step.done ? <Check className="h-4 w-4" aria-hidden="true" /> : i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="gs-step-title">{step.title}</div>
                  <div className="gs-step-hint">{step.hint}</div>
                </div>
                {step.done ? (
                  <span className="gs-chip">Done</span>
                ) : step.to ? (
                  <Link to={step.to} className="gs-link">{step.cta}</Link>
                ) : (
                  <button type="button" onClick={step.onClick} className="gs-link">{step.cta}</button>
                )}
              </li>
            ))}
          </ol>

          <div className="gs-foot">
            <button type="button" onClick={dismiss} className="gs-hide">Hide for now</button>
          </div>
        </section>
      </div>
    </div>
    {toastEl}
    </>
  )
}
